import React from 'react';
import _ from 'lodash';
import { Collapse } from '@blueprintjs/core';

import { ROSTER_DAILY_CONFIG } from '../roster-daily-config';
import { CustomersTimelineHeader } from './CustomersTimelineHeader';
import { Gridlines } from './Gridlines';
import { TimelineRow } from './TimelineRow';
import { IRosterCustomer, IRosterCustomerWithBookings } from '../../types';
import { OnAddBooking } from './add-booking-popover';
import { CustomerItems } from './customer-items';

interface CustomersTimelineProps {
  hours: string[];
  customers: IRosterCustomer[];
  bookings: IRosterCustomerWithBookings[];
  isCustomersOpen: boolean;
  currentDate?: Date;
  refreshData?: () => void;
  onAddBooking: OnAddBooking;
}

const EMPTY_ROW_COUNT = 1; // Change this to show empty rows whenever there's no data. A bit buggy, so setting it to 0 for now.

export function CustomersTimeline({
  hours,
  customers,
  bookings,
  isCustomersOpen,
  currentDate = new Date(),
  refreshData,
  onAddBooking,
}: CustomersTimelineProps) {
  const { HOUR_BLOCK_WIDTH, TIMELINE_ROW_HEIGHT } = ROSTER_DAILY_CONFIG;
  const rowWidth = _.size(hours) * HOUR_BLOCK_WIDTH;
  let gridRowCount = _.reduce(bookings, (acc, customer) => acc + _.size(customer.bookings), 0);
  const hasCustomers = _.size(customers) > 0;

  if (!hasCustomers) {
    gridRowCount = 2;
  }

  return (
    <div>
      <CustomersTimelineHeader rowWidth={rowWidth} />
      <Collapse isOpen={isCustomersOpen} keepChildrenMounted={false}>
        <div className='flex-column position-relative'>
          <Gridlines hours={hours} rowCount={gridRowCount} />
          {!hasCustomers &&
            _.times(EMPTY_ROW_COUNT, (idx) => (
              <TimelineRow rowWidth={rowWidth} key={idx} backgroundColor={'tertiary'} />
            ))}

          {customers.map((customer, idx) => {
            const customerBookings = _.find(bookings, (booking) => booking.customerId === customer.customerId);
            const bookingGroups =
              customerBookings && !_.isEmpty(customerBookings.bookings) ? customerBookings.bookings : [];
            const rowHeight =
              _.size(bookingGroups) > 0 ? TIMELINE_ROW_HEIGHT * _.size(bookingGroups) : TIMELINE_ROW_HEIGHT;

            return (
              <TimelineRow
                rowWidth={rowWidth}
                rowHeight={rowHeight}
                items={<CustomerItems bookings={bookingGroups} refreshData={refreshData} />}
                customer={customer}
                currentDate={currentDate}
                hasAddAction={true}
                onAddBooking={onAddBooking}
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={idx}
              />
            );
          })}
        </div>
      </Collapse>
    </div>
  );
}
